import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  MapPin, 
  Clock, 
  Users, 
  Calendar, 
  CheckCircle, 
  Star, 
  Briefcase,
  DollarSign,
  Building2
} from 'lucide-react';

const JobDetailsModal = ({ isOpen, onClose, job }) => {
  if (!job) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-accent-600 p-6 text-white relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {job.isUrgent && (
                      <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        Urgent
                      </span>
                    )}
                    {job.isFeatured && (
                      <span className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-semibold">
                        Featured
                      </span>
                    )}
                  </div>
                  <h2 className="text-3xl font-bold mb-2">{job.title}</h2>
                  <div className="flex items-center gap-2 text-white/90">
                    <Building2 className="h-5 w-5" />
                    <span className="text-xl">{job.company}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold mb-1">{job.salary}</div>
                  <div className="text-white/80">Salary Range</div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Job Details Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <MapPin className="h-6 w-6 text-primary-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Location</div>
                    <div className="text-gray-600">{job.location}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <Clock className="h-6 w-6 text-primary-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Job Type</div>
                    <div className="text-gray-600">{job.type}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <Users className="h-6 w-6 text-primary-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Experience</div>
                    <div className="text-gray-600">{job.experience}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <Calendar className="h-6 w-6 text-primary-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Posted</div>
                    <div className="text-gray-600">{job.postedDate}</div>
                  </div>
                </div>
              </div>

              {/* Job Description */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Job Description</h3>
                <div className="bg-blue-50 border-l-4 border-primary-600 p-6 rounded-r-xl">
                  <p className="text-gray-700 leading-relaxed text-lg">{job.description}</p>
                </div>
              </div>

              {/* Requirements and Benefits */}
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Requirements */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Key Requirements</h3>
                  <div className="space-y-3">
                    {job.requirements.map((requirement, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 leading-relaxed">{requirement}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Benefits */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Benefits & Perks</h3>
                  <div className="space-y-3">
                    {job.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <Star className="h-5 w-5 text-yellow-500 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 leading-relaxed">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Join Us?</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Briefcase className="h-6 w-6 text-primary-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Career Growth</h4>
                    <p className="text-gray-600 text-sm">Opportunities for professional development and advancement</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Users className="h-6 w-6 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Great Team</h4>
                    <p className="text-gray-600 text-sm">Work with talented professionals in a collaborative environment</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <DollarSign className="h-6 w-6 text-yellow-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Competitive Package</h4>
                    <p className="text-gray-600 text-sm">Attractive salary and comprehensive benefits package</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
                <div className="text-gray-600">
                  <p className="font-semibold">Ready to apply?</p>
                  <p className="text-sm">Submit your application through our Google Forms</p>
                </div>
                <div className="flex gap-4">
                  <motion.a
                    href="https://forms.gle/v4StRzRMGARiSKcR6"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gradient-to-r from-primary-600 to-accent-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-primary-700 hover:to-accent-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    Apply Now
                  </motion.a>
                  <motion.button
                    onClick={onClose}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="border-2 border-gray-300 text-gray-700 px-8 py-3 rounded-xl font-semibold hover:border-gray-400 hover:bg-gray-50 transition-all duration-300"
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default JobDetailsModal;



